import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, Share2, PlusSquare, Smartphone, X, Sparkles } from 'lucide-react';

const DISMISS_KEY = 'sta_install_banner_dismissed';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  return !!window.navigator?.standalone;
}

function isIOS() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function InstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(true); // start true, flip after mount check
  const [helpOpen, setHelpOpen] = useState(false);

  // Initial mount: read localStorage + standalone check
  useEffect(() => {
    const already = isStandalone();
    setInstalled(already);
    try {
      const flag = localStorage.getItem(DISMISS_KEY);
      setDismissed(flag === '1');
    } catch (_) {
      setDismissed(false);
    }
  }, []);

  // Listen for native install prompt availability + install event
  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBefore);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (prompt) {
      prompt.prompt();
      try {
        const choice = await prompt.userChoice;
        if (choice?.outcome === 'accepted') {
          // appinstalled event will fire and hide the banner
        }
      } catch (_) {
        // ignore
      }
      setPrompt(null);
      return;
    }
    // Fallback — iOS Safari / unsupported browsers
    setHelpOpen(true);
  }, [prompt]);

  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch (_) {
      // ignore storage errors
    }
    setDismissed(true);
  }, []);

  if (installed || dismissed) return null;

  return (
    <>
      <div
        className="relative z-50 border-b border-red-500/30 bg-gradient-to-r from-red-600 via-red-500 to-rose-600 text-white shadow-[0_6px_24px_-8px_rgba(239,68,68,0.6)]"
        data-testid="install-banner"
        role="region"
        aria-label="Install StudyTrack AI as an app"
      >
        <div className="mx-auto flex w-full max-w-6xl items-center gap-3 px-4 py-2.5 sm:px-6">
          <div className="hidden h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/15 ring-1 ring-white/30 sm:grid">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[13px] font-semibold leading-tight sm:text-sm">
              <Sparkles className="h-3.5 w-3.5 shrink-0" />
              <span>Install StudyTrack AI as an app</span>
            </div>
            <div className="truncate text-[11px] text-white/85 sm:text-xs">
              Faster launches, full-screen study mode and one-tap access from your home screen.
            </div>
          </div>
          <button
            type="button"
            onClick={handleInstall}
            data-testid="install-banner-cta"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-red-600 shadow-sm transition hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-red-600 sm:text-sm"
          >
            <Download className="h-4 w-4" />
            <span>Install</span>
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="install-banner-dismiss"
            aria-label="Dismiss install banner"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-white/85 transition hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Instructions dialog — iOS or browsers without beforeinstallprompt */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-md" data-testid="install-banner-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-red-500" />
              Add StudyTrack AI to your home screen
            </DialogTitle>
            <DialogDescription>
              Install the app on this device for faster launches, full-screen study mode and quick re-opens.
            </DialogDescription>
          </DialogHeader>
          {isIOS() ? (
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">1</span>
                <span>
                  Tap the <strong>Share</strong> icon
                  <Share2 className="mx-1 inline h-4 w-4 align-text-bottom" />
                  at the bottom of Safari.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">2</span>
                <span>
                  Scroll and tap <strong>Add to Home Screen</strong>
                  <PlusSquare className="mx-1 inline h-4 w-4 align-text-bottom" />.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">3</span>
                <span>Tap <strong>Add</strong> — the StudyTrack icon appears on your home screen.</span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">1</span>
                <span>
                  Open the <strong>browser menu</strong> (⋮ on Android · ⋯ on desktop Chrome / Edge).
                </span>
              </li>
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">2</span>
                <span>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">3</span>
                <span>Confirm — the app opens full-screen without browser bars.</span>
              </li>
            </ol>
          )}
          <div className="rounded-lg bg-neutral-900 p-3 text-xs text-neutral-400">
            💡 Tip: on Chrome / Edge / Samsung Internet you can also use the Install button in the top bar.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
